import NavItem from 'types/interfaces/NavItem';
import { IAbilitiesApi } from 'types/models/Abilities/Abilities';
import { NavSubjectNameEnum } from 'types/interfaces/sharedType.type';
import {
  transformNavConfigDependent,
  transformNavConfigHelper,
} from 'layouts/DashboardLayout/SideBar/NavSection/NavSection.helper';
import { CONFIG, GlobalVariables } from 'config/constant';
import { FetchArgs } from '@reduxjs/toolkit/dist/query';
import { generateApiKeySignature, generateNonce, encodeApiKey } from 'utils/api-key-security';

export const joinPaths = (paths: string[]): string => {
  return paths.join('/');
};

export const createPath = (path: string): string => {
  return `/${path}`;
};
export function extractRouteConfig(abilities: IAbilitiesApi[]): NavItem[] {
  // should return zone et secteur if both exist

  let hasZone: IAbilitiesApi | null = null;
  let hasSector: IAbilitiesApi | null = null;
  const newNavBarConfig = abilities
    ? abilities.map((ability) => {
        if (ability?.subjectName === NavSubjectNameEnum.Zones) hasZone = ability;
        if (ability?.subjectName === NavSubjectNameEnum.Sectors) hasSector = ability;

        return transformNavConfigHelper(ability);
      })
    : [];
  // filter to remove double dash from nav bar

  return [
    ...newNavBarConfig,
    ...transformNavConfigDependent({
      hasZone,
      hasSector,
    }),
  ].filter((item) => item.title !== GlobalVariables.DoubleDash);
}
function withoutTrailingSlash(url: string): string {
  return url.replace(/\/+$/, '');
}

function withoutLeadingSlash(url: string): string {
  return url.replace(/^\/+/, '');
}

function joinUrls(base: string, url: string): string {
  if (!base) return url;
  if (!url) return base;
  try {
    new URL(url);
    return url;
  } catch {
    // url is relative, join with base
  }
  const delimiter = base.endsWith('/') || !url.startsWith('?') ? '/' : '';
  return `${withoutTrailingSlash(base)}${delimiter}${withoutLeadingSlash(url)}`;
}

/**
 * Extract path and query string from URL for signature generation
 */
function extractPathAndQuery(url: string): { path: string; queryString: string } {
  try {
    const fullUrl = url.startsWith('http') ? url : joinUrls(CONFIG.BASE_URL_API, url);
    const urlObj = new URL(fullUrl);
    const path = urlObj.pathname;
    const queryString = urlObj.search ? urlObj.search.substring(1) : '';
    return { path, queryString: normalizeQueryString(queryString) };
  } catch {
    const [path, queryPart] = url.split('?');
    return { path: path || '/', queryString: normalizeQueryString(queryPart || '') };
  }
}

/**
 * Normalize query string by sorting parameters
 * Ensures consistency with backend's buildQueryString
 */
function normalizeQueryString(queryString: string): string {
  if (!queryString) {
    return '';
  }

  // Parse query string into object
  const params: Record<string, string[]> = {};

  queryString.split('&').forEach((pair) => {
    if (!pair) return;
    const [key, value] = pair.split('=');
    const decodedKey = decodeURIComponent(key);
    const decodedValue = decodeURIComponent(value || '');

    if (!params[decodedKey]) {
      params[decodedKey] = [];
    }
    params[decodedKey].push(decodedValue);
  });

  // Sort by keys and rebuild query string
  const sortedKeys = Object.keys(params).sort();
  return sortedKeys
    .map((key) => {
      const values = params[key];
      return values.map((v) => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`).join('&');
    })
    .join('&');
}

/**
 * Add API key security headers to request
 * Generates HMAC-SHA256 signature for request authentication
 */
export async function addApiKeyHeaders(
  headers: Headers,
  args: string | FetchArgs,
  method: string,
): Promise<void> {
  try {
    const apiKeySecret = CONFIG.API_KEY_SECRET;

    if (!apiKeySecret) {
      return;
    }

    // Extract path, query string, and body from args
    const isStringArg = typeof args === 'string';
    const url = isStringArg ? args : args.url || '';
    const bodyArg = !isStringArg ? args.body : undefined;

    const { path, queryString } = extractPathAndQuery(url);

    const nonce = generateNonce();
    const timestamp = Date.now();

    const signature = await generateApiKeySignature({
      httpMethod: method.toUpperCase(),
      requestPath: path,
      queryString,
      body: bodyArg,
      nonce,
      timestamp,
      secret: apiKeySecret,
      shouldIncludeBodyOrQuery: true,
    });

    // Encode nonce, timestamp, and signature into a single x-api-key header
    const encodedApiKey = encodeApiKey(nonce, timestamp, signature);
    headers.set('x-api-key', encodedApiKey);
  } catch (error) {
    // Log but don't fail - API key is optional
    console.warn('Failed to generate API key signature:', error);
  }
}
